import SidebarLayout from "components/Layout/Layout";
import { API_URL } from "env";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import type { PopconfirmProps } from 'antd';
import { Popconfirm, Pagination } from 'antd';

const StoreMainPage = () => {
    const [toolData, setToolData] = useState<{
        id: string;
        image: string;
        name: string;
        platform: string;
        star: number;
        review: number;
        price: string;
        discount?: string;
        finalPrice?: string;
    }[]>([]);

    const [page, setPage] = useState<number>(1);
    const [totalData, setTotalData] = useState<number>(0);

    async function fetchToolData(pageNumber: number = 1) {
        try {
            // Simulate fetching data from an API
            const res = await fetch(API_URL + `api/v1/be/tools?page=${pageNumber}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                },
            });
            const data = await res.json();
            if (data.status) {
                if (data.data.length === 0) {
                    setToolData([]);
                    return;
                }
                setTotalData(data.data.total || 0);
                setPage(pageNumber);
                const mapData = data.data.items.map((item: any) => ({
                    id: item.id,
                    image: item.image_url || 'https://placehold.co/600x400',
                    name: item.title?.en || 'No Name',
                    platform: item.platform || 'Unknown',
                    star: item.star || 0,
                    review: item.review || 0,
                    price: item.price || '0',
                    discount: item.price_discount || '0',
                    finalPrice: item.price_finalize || '0',
                }));
                setToolData(mapData);
            } else {
                toast.error(data.message || 'Failed to fetch data');
            }
        }
        catch (error) {
            toast.error('An error occurred while fetching data');
        }
    }

    async function DeleteItems(id: string) {
        const res = await fetch(API_URL + `api/v1/be/tools/${id}`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
            },
        });
        const data = await res.json();
        if (data.status) {
            toast.success('Item deleted successfully');
            fetchToolData();
        } else {
            toast.error(data.message || 'Failed to delete item');
        }
    }

    useEffect(() => {
        fetchToolData();
    }, []);
    return <SidebarLayout
        title={
            (
                <h4 className="text-2xl font-bold mb-4 text-gray-800 py-4">
                    Stores Dashboard
                </h4>
            )
        }
    >
        <div className="w-full px-4 py-2 bg-white rounded-lg shadow-md">
            <div className="flex justify-end mb-4">
                <button
                    onClick={() => window.location.href = '/stores/item?type=add'}
                    className="bg-blue-500 !text-white px-4 py-2 rounded hover:bg-blue-600 transition">
                    Add New Item
                </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {toolData.map((item) => (
                    <div key={item.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow duration-300">
                        <img src={item.image} alt={item.name} className="w-full h-48 object-cover" />
                        <div className="p-4">
                            <h3 className="text-lg font-semibold text-gray-800">{item.name} (id: {item.id})</h3>
                            <p className="text-sm text-gray-600 mb-2">Platform: {item.platform}</p>
                            <div className="flex items-center mb-2">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-star h-4 w-4 text-yellow-400 fill-current"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon></svg>
                                <span className="ml-1 text-sm font-medium">{item.star}</span>
                                <span className="ml-2 text-sm text-gray-500">({item.review} reviews)</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <div className="text-lg font-semibold text-gray-800">
                                    {item.discount && item.discount !== '0' ? (
                                        <div className="flex items-baseline space-x-2">
                                            <span className="text-lg font-semibold text-gray-800">${item.finalPrice}</span>
                                            <span className="text-sm text-gray-500 line-through">${item.price}</span>
                                            <span className="text-sm text-green-500">({item.discount}% off)</span>
                                        </div>
                                    ) : (
                                        <span className="text-lg font-semibold text-gray-800">${item.price}</span>
                                    )}
                                </div>
                                <div className="flex gap-4">
                                    <button
                                        onClick={() => window.location.href = `/stores/item?type=review&id=${item.id}`}
                                        className="bg-green-500 !text-white px-3 py-1 rounded hover:bg-green-600 transition">
                                        Preview
                                    </button>
                                    <button
                                        onClick={() => window.location.href = `/stores/item?type=edit&id=${item.id}`}
                                        className="bg-yellow-500 !text-white px-3 py-1 rounded hover:bg-yellow-600 transition">
                                        Edit
                                    </button>
                                    <Popconfirm
                                        title="Delete the Item"
                                        description="Are you sure to delete this item?"
                                        onConfirm={() => DeleteItems(item.id)}
                                        okText="Yes"
                                        cancelText="No"
                                    >
                                        <button
                                            className="bg-red-500 !text-white px-3 py-1 rounded hover:bg-red-600 transition">
                                            Delete
                                        </button>
                                    </Popconfirm>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
                <Pagination
                    className="mt-4 col-span-full flex justify-center"
                    current={page}
                    pageSize={10}
                    total={totalData}
                    onChange={(page) => fetchToolData(page)}
                    showSizeChanger={false}
                />
            </div>
        </div>
    </SidebarLayout>;
}

export default StoreMainPage;
